
import { useState, useEffect, ReactNode } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setSidebarCollapsed(true);
    } else {
      setSidebarOpen(true);
      // Retrieve previously saved preference for desktop
      const savedCollapsed = localStorage.getItem('sidebarCollapsed');
      if (savedCollapsed) {
        setSidebarCollapsed(savedCollapsed === 'true');
      }
    }
  }, [isMobile]);

  // Save preference when collapsed state changes (only on desktop)
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
    }
  }, [sidebarCollapsed, isMobile]);

  const toggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar 
        open={sidebarOpen} 
        setOpen={setSidebarOpen} 
        collapsed={sidebarCollapsed}
        toggleCollapse={toggleCollapse}
      />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        !sidebarOpen && !isMobile ? 'ml-[60px]' : 
        (sidebarOpen && sidebarCollapsed && !isMobile) ? 'ml-[80px]' : ''
      }`}>
        <Header 
          sidebarOpen={sidebarOpen} 
          setSidebarOpen={setSidebarOpen} 
          collapsed={sidebarCollapsed}
          toggleCollapse={toggleCollapse}
        />
        <main className="flex-1 overflow-y-auto p-6 md:p-6 sm:p-4">
          <div className="mx-auto max-w-7xl animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
